import { useEffect, useMemo, useRef, useState } from "react";

import { AnimatePresence } from "framer-motion";

import { usePageTitle } from "@/atoms/page-title-atom";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { BenchmarkSettings } from "@features/benchmarks";
import { ClicksAndViewsEnhancedModellingBanner } from "@features/clicks-and-view-enhanced-modelling/clicks-and-views-enhanced-modelling";
import { CustomMetricsProvider } from "@features/custom-metrics/hooks/custom-metrics-provider";
import {
  Charts,
  Flyout,
  MinimizedTopBar,
  SalesPageV3Provider,
  SalesTable,
  TopLevelFilters,
} from "@features/sales-v3";
import { type ChartsImperativeRef } from "@features/sales-v3/charts/charts";
import { usePageConfiguration, useSavedViews } from "@features/sales-v3/hooks";
import { SalesSavedViewsProvider } from "@features/sales-v3/hooks/use-saved-views-queries";
import { SalesEducationModal } from "@features/sales-v3/sales-education-modal";
import { SalesV3Provider } from "@features/sales-v3/table/contexts";
import { useIntersectionObserver } from "@hooks/use-intersection-observer";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";

const TopBar = ({
  chartsRef,
  onShowConfigurationDetailsModal,
}: {
  chartsRef: React.MutableRefObject<ChartsImperativeRef | null>;
  onShowConfigurationDetailsModal: () => void;
}) => {
  const [topLevelFiltersContainerElRef, isMinimizedTopBarVisible] =
    useIntersectionObserver();
  return (
    <div>
      <AnimatePresence>
        {isMinimizedTopBarVisible && <MinimizedTopBar />}
      </AnimatePresence>
      <div ref={topLevelFiltersContainerElRef} className="px-6">
        <TopLevelFilters
          chartsRef={chartsRef}
          onShowConfigurationDetailsModal={onShowConfigurationDetailsModal}
        />
      </div>
    </div>
  );
};

export const SalesV3 = () => {
  const [, setPageTitle] = usePageTitle();
  const { user } = useUser();

  useEffect(() => {
    setPageTitle("Sales");
  }, [setPageTitle]);

  useEffect(() => {
    prettyLogEvent.visited({
      page: Pages.SALES,
      user,
    });
  }, [user]);

  return (
    <CustomMetricsProvider>
      <SalesSavedViewsProvider>
        <SalesV3Provider>
          <SalesPageV3Provider>
            <Content />
            <SalesEducationModal />
          </SalesPageV3Provider>
        </SalesV3Provider>
      </SalesSavedViewsProvider>
    </CustomMetricsProvider>
  );
};

const Content = () => {
  const { user } = useUser();
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    dateRange,
    dateRangeInterval,
    compareDateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    flyoutId,
    flyoutTab,
    setFlyoutId,
    setFlyoutTab,
  } = usePageConfiguration();

  const [isConfigurationDetailsModalOpen, setIsConfigurationDetailsModalOpen] =
    useState(false);

  const chartsRef = useRef<ChartsImperativeRef | null>(null);

  const { selectedSavedView } = useSavedViews();

  const initialLoad = useRef(true);

  useEffect(() => {
    if (initialLoad.current && selectedSavedView) {
      const pc = selectedSavedView.config.pageConfiguration;

      const pageContext = {
        dateRange: pc.dateRange,
        attributionModel: pc.attributionModel,
        attributionWindow: pc.attributionWindow,
        accountingMode: pc.accountingMode,
        timeGranularity: pc.timeGranularity,
        compareDateRange: pc.compareDateRange,
        breakdowns: pc.breakdowns,
        filters: pc.filters,
        savedVewId: selectedSavedView.id,
      };

      prettyLogEventPage({
        domain: "Initial state",
        action: "onMount",
        additionalActionInfo: "Initial state",
        data: {
          pageContext,
        },
      });

      initialLoad.current = false;
    }
  }, [selectedSavedView]);

  const flyoutConfig = useMemo(() => {
    return {
      dateRange,
      dateRangeInterval,
      compareDateRange,
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      flyoutId,
      flyoutTab,
      setFlyoutId,
      setFlyoutTab,
    };
  }, [
    dateRange,
    dateRangeInterval,
    compareDateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    flyoutId,
    flyoutTab,
    setFlyoutId,
    setFlyoutTab,
  ]);

  return (
    <div>
      <Flyout config={flyoutConfig} />
      <div className=" pb-6 bg-white border-b border-gray-0">
        <ClicksAndViewsEnhancedModellingBanner />
        <TopBar
          chartsRef={chartsRef}
          onShowConfigurationDetailsModal={() => {
            prettyLogEventPage({
              domain: "PageConfiguration",
              action: "onClick",
              additionalActionInfo: "Page Configuration Modal opened",
            });

            setIsConfigurationDetailsModalOpen(true);
          }}
        />
        <div className="px-6">
          <Charts ref={chartsRef} />
          <SalesTable
            isConfigurationDetailsModalOpen={isConfigurationDetailsModalOpen}
            onCloseConfigurationDetailsModal={() => {
              prettyLogEventPage({
                domain: "PageConfiguration",
                action: "onClose",
                additionalActionInfo: "Configuration Details Modal closed",
              });

              setIsConfigurationDetailsModalOpen(false);
            }}
          />
        </div>
        {user.featureFlags.showBenchmarks &&
          !user.featureFlags.enableUniversalBenchmarks && (
            <div className="mx-6 my-11">
              <BenchmarkSettings />
            </div>
          )}
      </div>
    </div>
  );
};
